import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
interface StepCardProps {
  number: number;
  emoji: string;
  title: string;
  description: string;
  index: number;
  isLast: boolean;
}
function StepCard({
  number,
  emoji,
  title,
  description,
  index,
  isLast
}: StepCardProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <div className="relative" data-id="element-110">
      <motion.div ref={ref} className="relative z-10 p-6 sm:p-8 rounded-2xl bg-white border border-rose-100 shadow-lg text-center" initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      delay: index * 0.15
    }} data-id="element-111">
        {/* Step number badge */}
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md" data-id="element-112">
          {number}
        </div>

        <div className="text-4xl sm:text-5xl mb-4 mt-2" data-id="element-113">{emoji}</div>
        <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-114">{title}</h3>
        <p className="text-slate-600 text-sm leading-relaxed" data-id="element-115">{description}</p>
      </motion.div>

      {/* Connector arrow (hidden on last item and mobile) */}
      {!isLast && <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-0" data-id="element-116">
          <motion.div className="text-rose-300 text-2xl" initial={{
        opacity: 0,
        x: -10
      }} animate={isInView ? {
        opacity: 1,
        x: 0
      } : {}} transition={{
        duration: 0.3,
        delay: index * 0.15 + 0.3
      }} data-id="element-117">
            →
          </motion.div>
        </div>}
    </div>;
}
export function HowItWorks() {
  const steps = [{
    emoji: '📥',
    title: '1. Compra & Descarga',
    description: 'Acceso inmediato a las 26 carpetas con +25.000 diseños.'
  }, {
    emoji: '✏️',
    title: '2. Edita en Canva',
    description: 'Personaliza colores, textos y tu marca en minutos. ¡Canva es gratis!'
  }, {
    emoji: '🖨️',
    title: '3. Imprime o Envía',
    description: 'Imprime en casa o envía el archivo digital a tu cliente al instante.'
  }, {
    emoji: '💰',
    title: '4. Cobra y Repite',
    description: 'Quédate con el 100% de tus ganancias. Sin límite de ventas.'
  }];
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, {
    once: true
  });
  return <section className="w-full bg-white py-20 sm:py-28" data-id="element-118">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-119">
        {/* Header */}
        <motion.div ref={headerRef} className="text-center mb-16" initial={{
        opacity: 0,
        y: 30
      }} animate={isHeaderInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} data-id="element-120">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 mb-4" data-id="element-121">
            🚀 Cómo funciona
          </h2>
          <p className="text-xl sm:text-2xl font-bold text-rose-500 mb-4" data-id="element-122">
            En 4 Pasos ya Estás Vendiendo 🎉
          </p>
          <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-123">
            No necesitas experiencia en diseño. Si sabes usar Canva, puedes
            empezar hoy mismo.
          </p>
          <p className="mt-4 text-lg sm:text-xl font-extrabold" data-id="element-124">
            <span className="text-rose-500" data-id="element-125">Diseñá hoy.</span>{' '}
            <span className="text-pink-400" data-id="element-126">Imprimí mañana.</span>{' '}
            <span className="bg-gradient-to-r from-amber-500 to-amber-400 bg-clip-text text-transparent" data-id="element-127">
              Ganá siempre.
            </span>
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6" data-id="element-128">
          {steps.map((step, index) => <StepCard key={step.title} number={index + 1} emoji={step.emoji} title={step.title} description={step.description} index={index} isLast={index === steps.length - 1} data-id="element-129" />)}
        </div>

        {/* Price reminder */}
        <motion.div className="mt-14 text-center" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} data-id="element-130">
          <a href="https://magiadigital.online/checkouts/cn/hWN9TYd7nIHOv98RJ6pjImQM/es-ar?_r=AQABPAMwqwcFHsaNx77xI9-vQVNjUUJUKqqMcQTwOKcBl4s&preview_theme_id=158917099778" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300" data-id="element-131">
            <span data-id="element-132">🛒 Todo esto por solo</span>
            <span className="text-2xl font-extrabold" data-id="element-133">$27.990 ARS</span>
          </a>
        </motion.div>
      </div>
    </section>;
}